import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import SearchIcon from '@mui/icons-material/Search';
import SystemUpdateAltRoundedIcon from '@mui/icons-material/SystemUpdateAltRounded';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { GridToolbarColumnsButton, GridToolbarDensitySelector } from '@mui/x-data-grid-premium';
import { useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { differenceInDays } from 'date-fns';
import { format } from 'date-fns-tz';
import { saveAs } from 'file-saver';
import { useSnackbar } from 'notistack';
import { ChangeEvent, CompositionEvent, FC, memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import ToolbarContainer from 'src/components/ToolbarContainer';
import { Calendar } from 'src/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover';
import { fiveZeroZeroErrorMessage } from 'src/constants/messages';
import LicenseContext from 'src/contexts/LicenseContext';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import { queryKey } from 'src/hooks/useOrders.request';
import { SHIPMENT_ORDERS_QUERY_KEY } from 'src/hooks/useQueryShipmentOrders';
import { cn } from 'src/utils/css.util';

import MyGridToolbarFilterButton from '../common/MyGridToolbarFilterButton';

export type CustomToolbarProps = {
  startOn: Date;
  endOn: Date;
  removeSelectedOrders: () => Promise<void>;
  toggleDialogIsOpen: () => void;
  setIsLoading: (prevState: boolean) => boolean;
  isLoading: boolean;
  canDeleteAll: boolean;
  deleteAll: (settledFnk: () => void) => void;
  handleDownloadExcel: (version?: string) => void;
  globalSearchText?: string;
  onGlobalSearchChange?: (value: string) => void;
  rowHeight?: number;
  onRowHeightChange?: (height: number) => void;
};

const CustomToolbar: FC<CustomToolbarProps> = memo(
  ({
    startOn,
    endOn,
    removeSelectedOrders,
    toggleDialogIsOpen,
    setIsLoading,
    isLoading,
    canDeleteAll,
    deleteAll,
    handleDownloadExcel,
    globalSearchText,
    onGlobalSearchChange,
  }) => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const licenseContext = useContext(LicenseContext);

    const queryClient = useQueryClient();
    const [canUseUpload, setCanUseUpload] = useState<boolean>(false);
    const [deleteHandlerDialogIsOpen, setDeleteHandlerDialogIsOpen] = useState<boolean>(false);
    const [isComposing, setIsComposing] = useState(false);
    const [localSearchText, setLocalSearchText] = useState(globalSearchText || '');

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      // 日本語入力の変換中でない場合のみ検索を実行
      if (!isComposing) {
        onGlobalSearchChange?.(value);
      }
      setLocalSearchText(value);
    };

    const handleCompositionStart = () => {
      setIsComposing(true);
    };

    const handleCompositionEnd = (event: CompositionEvent<HTMLInputElement>) => {
      setIsComposing(false);
      // 変換確定時に検索を実行
      const target = event.target as HTMLInputElement;
      onGlobalSearchChange?.(target.value);
    };

    const deleteHandlerDialogOnClose = () => {
      setDeleteHandlerDialogIsOpen(false);
    };

    const deleteBtnOnClick = () => {
      if (canDeleteAll) {
        setDeleteHandlerDialogIsOpen(true);

        return;
      }

      void removeSelectedOrders().finally(() => {
        setDeleteHandlerDialogIsOpen(false);
      });
    };

    useEffect(() => {
      if (!licenseContext.config) return;

      setCanUseUpload(licenseContext.config.selected_company_id !== 0);
    }, [licenseContext.config]);

    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    const dateRangeLabel = useMemo(() => {
      if (!startOn || !endOn) return '日付を選択';
      const formattedStart = format(startOn, 'yyyy/MM/dd', { timeZone: 'Asia/Tokyo' });
      const formattedEnd = format(endOn, 'yyyy/MM/dd', { timeZone: 'Asia/Tokyo' });
      return `${formattedStart} ~ ${formattedEnd}`;
    }, [startOn, endOn]);

    const [importFile, setImportFile] = useState<File | undefined>(undefined);
    const importButtonOnClick = (event: ChangeEvent<HTMLInputElement>) => {
      setImportFile(event.target.files[0]);
    };

    const [importForUpdateFile, setImportForUpdateFile] = useState<File | undefined>(undefined);
    const importForUpdateButtonOnClick = (event: ChangeEvent<HTMLInputElement>) => {
      setImportForUpdateFile(event.target.files[0]);
    };

    useEffect(() => {
      if (!importFile) return;

      const requestPath = '/api/v2/orders/import';

      const data = new FormData();
      data.append('file', importFile);

      setIsLoading(true);
      enqueueSnackbar('インポートを開始します');

      axios
        .post(requestPath, data, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then(
          (response: AxiosResponse<{ severity: string; message: string }>) => {
            enqueueSnackbar(response.data.message);
          }
        )
        .catch((e) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (e.response.status === 400) {
            enqueueSnackbar(
              '取り込めない注文がありました、データをご確認ください。'
            );
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
            const blob = new Blob([e.response.data], {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
              type: e.response.data.type
            });
            saveAs(blob, 'errors.txt');
          } else {
            enqueueSnackbar(fiveZeroZeroErrorMessage);
          }
        })
        .finally(() => {
          setImportFile(undefined);
          setIsLoading(false);
          void queryClient.invalidateQueries(['orders']);
          void queryClient.invalidateQueries([queryKey]);
          selectImportMethodDialogOnClose();
        });
    }, [enqueueSnackbar, importFile]);

    useEffect(() => {
      if (!importForUpdateFile) return;

      const requestPath = '/api/v5/shipment_orders/import_for_update';

      const data = new FormData();
      data.append('file', importForUpdateFile);

      setIsLoading(true);
      enqueueSnackbar('インポートを開始します');

      axios.post(requestPath, data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(
        (response: AxiosResponse<{ severity: string; message: string, warnings: string[] }>) => {
          enqueueSnackbar(response.data.message);
          if (response.data.warnings.length > 0) {
            enqueueSnackbar(response.data.warnings.join('\n'), { style: { whiteSpace: 'pre-line' } });
          }
        }
      ).catch((e) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (e.response.status === 400) {
          enqueueSnackbar(
            '取り込めない注文がありました、データをご確認ください。'
          );
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
          const blob = new Blob([e.response.data], {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
            type: e.response.data.type
          });
          saveAs(blob, 'errors.txt');
        } else {
          enqueueSnackbar(fiveZeroZeroErrorMessage);
        }
      })
      .finally(() => {
        setImportForUpdateFile(undefined);
        setIsLoading(false);
        void queryClient.invalidateQueries(['orders']);
        void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDERS_QUERY_KEY] });
        void queryClient.resetQueries([queryKey]);
        selectImportMethodDialogOnClose();
      });
    }, [enqueueSnackbar, importForUpdateFile, queryClient, setIsLoading,]);

    const [selectImportMethodDialogIsOpen, setSelectImportMethodDialogIsOpen] = useState<boolean>(false);
    const selectImportMethodButtonOnClick = () => {
      setSelectImportMethodDialogIsOpen(true);
    };
    const selectImportMethodDialogOnClose = () => {
      setSelectImportMethodDialogIsOpen(false);
    };

    const [exportVersionDialogIsOpen, setExportVersionDialogIsOpen] = useState<boolean>(false);
    const exportButtonOnClick = () => {
      // ロジスティード相模原の場合はダイアログを表示
      if (licenseContext.config?.company_name?.includes('ロジスティード') && licenseContext.config?.company_name?.includes('相模原')) {
        setExportVersionDialogIsOpen(true);
      } else {
        handleDownloadExcel();
      }
    };
    const exportVersionDialogOnClose = () => {
      setExportVersionDialogIsOpen(false);
    };
    const exportNewVersionOnClick = () => {
      handleDownloadExcel();
      exportVersionDialogOnClose();
    };
    const exportLegacyVersionOnClick = () => {
      handleDownloadExcel('logisteed');
      exportVersionDialogOnClose();
    };

    const customImportButtonOnClick = () => {
      void navigate('/import');
    };

    const [startDate, setStartDate] = useState<Date | null>(startOn || null);
    const [endDate, setEndDate] = useState<Date | null>(endOn || null);
    const onCommitDateRange = () => {
      if (!startDate || !endDate) return;

      if (differenceInDays(endDate, startDate) < 0) {
        enqueueSnackbar('終了日は開始日より過去に設定できません', { variant: 'warning' });
        return;
      }

      if (differenceInDays(endDate, startDate) > 30) {
        enqueueSnackbar('最大取得可能日数の31日を超過しています', { variant: 'warning' });
        return;
      }

      setIsDatePickerOpen(false);
      const start = datetimeDecorator.toYyyyMmDd(startDate);
      const end = datetimeDecorator.toYyyyMmDd(endDate);
      void navigate(`/shipment-orders/${start}/${end}`);
    };

    // ツールバーの高さを管理するstate
    const [toolbarHeight, setToolbarHeight] = useState<number>(64);
    // ツールバー要素への参照
    const toolbarRef = useRef<HTMLDivElement>(null);

    // 左側グループのサイズを管理するstate
    const [leftGroupSize, setLeftGroupSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
    // 左側グループ要素への参照
    const leftGroupRef = useRef<HTMLDivElement>(null);

    // ツールバーの高さを監視して、変更があれば更新する
    useEffect(() => {
      if (leftGroupSize.height === 0) return;

      if (toolbarHeight < leftGroupSize.height) {
        setToolbarHeight((prev) => (prev * 2));
      } else if ((toolbarHeight / 2) > leftGroupSize.height) {
        setToolbarHeight((prev) => Math.max(64, prev / 2));
      }
    }, [leftGroupSize, toolbarHeight]);

    // 左側グループのサイズを監視して、変更があれば更新する
    useEffect(() => {
      if (!leftGroupRef.current) return;

      // ResizeObserverを使用して左側グループのサイズ変更を検出
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          // 左側グループの実際の幅と高さを取得
          const newWidth = entry.contentRect.width;
          const newHeight = entry.contentRect.height;
          // サイズが変わった場合のみ更新（パフォーマンス最適化）
          if (newWidth !== leftGroupSize.width || newHeight !== leftGroupSize.height) {
            setLeftGroupSize({ width: newWidth, height: newHeight });
          }
        }
      });

      // 左側グループ要素の監視を開始
      resizeObserver.observe(leftGroupRef.current);

      // クリーンアップ: コンポーネントがアンマウントされたら監視を停止
      return () => {
        resizeObserver.disconnect();
      };
    }, [leftGroupSize]);

    return (
      <>
        <ToolbarContainer sx={{ minHeight: `${toolbarHeight}px !important` }}>
          <Dialog
            open={selectImportMethodDialogIsOpen}
            onClose={selectImportMethodDialogOnClose}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>インポート形式を選択してください</DialogTitle>
            <DialogContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <LoadingButton
                  fullWidth
                  variant="outlined"
                  loading={isLoading}
                  onClick={customImportButtonOnClick}
                >
                  新規インポート
                </LoadingButton>
                <LoadingButton
                  fullWidth
                  variant="outlined"
                  loading={isLoading}
                  disabled={isLoading || !canUseUpload}
                >
                  上書きインポート (エクスポートしたファイルを利用してください)
                  <input
                    type="file"
                    disabled={isLoading || !canUseUpload}
                    onChange={importForUpdateButtonOnClick}
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    style={{
                      opacity: 0,
                      appearance: 'none',
                      position: 'absolute',
                      width: '100%',
                      cursor: 'pointer'
                    }}
                  />
                </LoadingButton>
              </Stack>
            </DialogContent>
          </Dialog>
          <Dialog
            open={exportVersionDialogIsOpen}
            onClose={exportVersionDialogOnClose}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>エクスポートバージョンを選択してください</DialogTitle>
            <DialogContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ pt: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={exportNewVersionOnClick}
                >
                  新バージョン
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={exportLegacyVersionOnClick}
                >
                  旧バージョン
                </Button>
              </Stack>
            </DialogContent>
          </Dialog>
          <Box
            ref={toolbarRef}
            sx={{
              width: '100%',
              px: 2,
              py: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {/* ツールバーのメインコンテナ - 左側と右側のグループを配置 */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              sx={{ width: '100%' }}
            >
              {/* 左側グループ: 日付選択、検索、フィルター系ボタン */}
              <Stack
                ref={leftGroupRef}
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                flexWrap="wrap"
                rowGap={2}
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setIsDatePickerOpen(true)}
                      sx={{ minWidth: 200 }}
                    >
                      {dateRangeLabel}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Box sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        <Typography variant="subtitle2" fontWeight="bold">日付範囲を選択</Typography>
                        <Calendar
                          mode="range"
                          selected={{ from: startDate, to: endDate }}
                          onSelect={(range) => {
                            if (range?.from) setStartDate(range.from);
                            if (range?.to) setEndDate(range.to);
                            if (range?.from && !range?.to) {
                              setEndDate(range.from); // Single date selection
                            }
                          }}
                          defaultMonth={startOn || undefined}
                          numberOfMonths={2}
                          className={cn("p-3 pointer-events-auto")}
                        />
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            onClick={() => {
                              setStartDate(undefined);
                              setEndDate(undefined);
                            }}
                          >
                            クリア
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={onCommitDateRange}
                          >
                            適用
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  </PopoverContent>
                </Popover>
                <TextField
                  size="small"
                  placeholder="検索..."
                  value={localSearchText || globalSearchText || ''}
                  onChange={handleSearchChange}
                  onCompositionStart={handleCompositionStart}
                  onCompositionEnd={handleCompositionEnd}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{ minWidth: { xs: '100%', md: 220 } }}
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  <MyGridToolbarFilterButton />
                  <GridToolbarColumnsButton />
                  <GridToolbarDensitySelector />
                </Stack>
              </Stack>

              {/* 右側グループ: インポート、エクスポート、削除、新規作成ボタン */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                sx={{
                  justifyContent: { xs: 'flex-end', md: 'flex-end' },
                  width: { xs: '100%', md: 'auto' }
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CloudUploadRoundedIcon />}
                  onClick={selectImportMethodButtonOnClick}
                >
                  インポート
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SystemUpdateAltRoundedIcon />}
                  onClick={exportButtonOnClick}
                >
                  エクスポート
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<DeleteForeverOutlinedIcon />}
                  onClick={deleteBtnOnClick}
                >
                  削除
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddCircleOutlinedIcon />}
                  onClick={toggleDialogIsOpen}
                >
                  新規作成
                </Button>
              </Stack>
            </Stack>
          </Box>
        </ToolbarContainer>
        <Dialog
          open={deleteHandlerDialogIsOpen}
          onClose={deleteHandlerDialogOnClose}
        >
          <DialogTitle>
            削除する対象を選択してください
          </DialogTitle>
          <DialogActions>
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteForeverRoundedIcon />}
              onClick={() => {
                deleteAll(deleteHandlerDialogOnClose);
              }}
            >
              すべて削除する
            </Button>
            <Button
              onClick={() => {
                void removeSelectedOrders().finally(() => {
                  setDeleteHandlerDialogIsOpen(false);
                });
              }}
              variant="outlined"
              startIcon={<DeleteSweepRoundedIcon />}
            >
              選択したものを削除する
            </Button>
            <Button
              onClick={deleteHandlerDialogOnClose}
              startIcon={<HighlightOffRoundedIcon />}
            >
              キャンセル
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default CustomToolbar;
